import { useMemo } from "react";
import t from "@/lang/lang";
import { MonitorDetailSummaryComponent } from "@/routes/processes/[processId]/qpm/requirements/[requirementId]/_components/monitoring/monitor-summary-component";
import {
  Area,
  Building,
  Level,
} from "@/services/backend/qpm/buildings/types/building";
import {
  MonitorIoTClass,
  MonitorType,
} from "@/services/backend/qpm/types/enums";
import { RequirementDTO } from "@/services/backend/qpm/requirements/types/requirement-dto";
import { RequirementMonitor } from "@/services/backend/qpm/requirements/types/requirement-monitor";
import {
  RequirementMonitorSummaryDTO,
  RequirementMonitorSummaryItemDTO,
  TypeSummaryItemDTO,
} from "@/services/backend/qpm/requirements/types/requirement-monitor-summary";
import { MonitorSnapDTO } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { accumulateDerivedIoTDependents } from "@/services/backend/qpm/requirements/lib/derived-iot-dependents";

export type LevelMonitorSummaryComponentProps = {
  requirement: RequirementDTO;
  selectedBuilding: Building;
  selectedLevel: Level;
  selectedLevelAreas: Area[];
};

export function LevelMonitorSummaryComponent({
  requirement,
  selectedBuilding,
  selectedLevel,
  selectedLevelAreas,
}: LevelMonitorSummaryComponentProps) {
  // We perform this calculation on the client because the summary must be available offline,
  // is meant only for quick self-checks rather than backend storage,
  // and avoids duplicating the full overview component that handles all persisted data.
  const selectedMonitorSummary: RequirementMonitorSummaryDTO = useMemo(() => {
    const selectedAreaIDs = new Set(
      (selectedLevelAreas || []).map((area) => area.base.id),
    );
    const isInSelectedLevel = (monitor: RequirementMonitor) =>
      selectedAreaIDs.has(monitor.areaId);

    const levelMonitors =
      requirement.selectedMonitors.filter(isInSelectedLevel);

    const typeSummaryByKey: Record<string, TypeSummaryItemDTO> = {};
    const incType = (
      type: MonitorType,
      amount: number,
      cls?: MonitorIoTClass,
    ) => {
      const key = `${type}|||${cls ?? ""}`;
      const existing = typeSummaryByKey[key];
      if (existing) {
        existing.amount += amount;
        return;
      }
      typeSummaryByKey[key] = { monitorType: type, iotClass: cls, amount };
    };

    const resultMonitors: RequirementMonitorSummaryItemDTO[] = [];

    const upsertMonitorSummary = (
      item: Omit<RequirementMonitorSummaryItemDTO, "totalAmount"> & {
        totalAmount: number;
      },
    ) => {
      const idx = resultMonitors.findIndex(
        (m) => m.monitorId === item.monitorId,
      );
      if (idx >= 0) {
        const existing = resultMonitors[idx];
        existing.totalAmount += item.totalAmount;
        existing.totalInstallationTime += item.totalInstallationTime;
        existing.totalServiceTime += item.totalServiceTime;
        existing.totalPrice += item.totalPrice;
      } else {
        resultMonitors.push({ ...item });
      }
    };

    let totalInstallationTime = 0;
    let totalServiceTime = 0;
    let totalPrice = 0;

    // Track IoT roles to create derived dependents
    let requiredSimAmount = 0;
    let requiredPortalAmount = 0;

    levelMonitors.forEach((monitor) => {
      const snap = requirement.monitorSnaps[monitor.monitorId];
      if (!snap) {
        incType(monitor.monitorTypeKey, monitor.amount);
        return;
      }

      const install = (snap.times?.install ?? 0) * monitor.amount;
      const service = (snap.times?.maintain ?? 0) * monitor.amount;
      const basePrice = snap.basePrice?.price ?? 0;
      const price = basePrice * monitor.amount;

      totalInstallationTime += install;
      totalServiceTime += service;
      totalPrice += price;

      incType(monitor.monitorTypeKey, monitor.amount);

      const iotClass = monitor.ioTSpec?.class ?? snap.capabilities?.iot?.class;
      if (monitor.monitorTypeKey === MonitorType.IoT && iotClass) {
        incType(MonitorType.IoT, monitor.amount, iotClass);

        const next = accumulateDerivedIoTDependents(
          {
            requiredSimAmount,
            requiredPortalAmount,
          },
          iotClass,
          monitor.amount,
        );
        requiredSimAmount = next.requiredSimAmount;
        requiredPortalAmount = next.requiredPortalAmount;
      }

      upsertMonitorSummary({
        monitorId: monitor.monitorId,
        monitorTypeKey: monitor.monitorTypeKey,
        iotClass,
        hasLethalAgent: monitor.hasLethalAgent,
        totalAmount: monitor.amount,
        totalInstallationTime: install,
        totalServiceTime: service,
        basePrice,
        totalPrice: price,
      });
    });

    const findReferenceSnapByIoTClass = (
      cls: MonitorIoTClass,
    ): MonitorSnapDTO | undefined =>
      Object.values(requirement.monitorSnaps ?? {}).find(
        (snap) => snap.capabilities?.iot?.class === cls,
      );

    const addDerivedIoTItem = (
      cls: MonitorIoTClass,
      amount: number,
      fallbackNameKey: string,
    ) => {
      if (amount <= 0) return;

      const refSnap = findReferenceSnapByIoTClass(cls);
      const install = (refSnap?.times?.install ?? 0) * amount;
      const service = (refSnap?.times?.maintain ?? 0) * amount;
      const basePrice = refSnap?.basePrice?.price ?? 0;
      const price = basePrice * amount;

      // incType(MonitorType.IoT, amount);
      incType(MonitorType.IoT, amount, cls);

      totalInstallationTime += install;
      totalServiceTime += service;
      totalPrice += price;

      const monitorId = refSnap?.sourceId ?? `__derived_${fallbackNameKey}`;

      upsertMonitorSummary({
        monitorId,
        monitorTypeKey: MonitorType.IoT,
        iotClass: cls,
        hasLethalAgent: false,
        totalAmount: amount,
        totalInstallationTime: install,
        totalServiceTime: service,
        basePrice,
        totalPrice: price,
      });
    };

    // - SIM: one per gateway and one per IoTPestMonitorWithSim
    // - PORTAL: one per endpoint (sensor + pest monitor)
    addDerivedIoTItem(MonitorIoTClass.IoTSim, requiredSimAmount, "SIM");
    addDerivedIoTItem(
      MonitorIoTClass.IoTPortal,
      requiredPortalAmount,
      "PORTAL",
    );

    return {
      monitors: resultMonitors,
      typeSummary: Object.values(typeSummaryByKey),
      totalInstallationTime,
      totalServiceTime,
      totalPrice: totalPrice !== 0 ? totalPrice : undefined,
    };
  }, [requirement, selectedLevelAreas]);

  return (
    <MonitorDetailSummaryComponent
      title={`${t("Gesamtübersicht")} ${selectedBuilding.name} - ${selectedLevel.base.name}`}
      monitorSummary={selectedMonitorSummary}
      monitorRecords={requirement.monitorSnaps}
    />
  );
}
